/**
 * Minimal HTML → Word (.docx) builder.
 *
 * A .docx file is just a ZIP of XML parts. We build the smallest set that Word,
 * Google Docs and LibreOffice all open cleanly: the content-type map, the
 * package relationships, a stylesheet, and one document body. The cleaned HTML
 * is walked block by block and turned into Word paragraphs and text runs.
 *
 * This is deliberately a readable *subset*, not a full HTML renderer: headings,
 * paragraphs, bold / italic / underline, links, ordered + bullet lists,
 * blockquotes, code, horizontal rules, and tables flattened to tab-separated
 * lines. Anything fancier degrades to plain paragraphs rather than breaking the
 * file. Everything runs in the tab — nothing is uploaded.
 */

import { parseToBody } from './sanitize';

/** The official MIME type for a .docx package. */
export const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// ── Walk model ────────────────────────────────────────────────────────────────

interface Run {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  mono?: boolean;
  /** Relationship id of the hyperlink this run belongs to, if any. */
  linkId?: string;
  /** A line break within a paragraph (`<br>`), rendered as `<w:br/>`. */
  br?: boolean;
}

interface Para {
  /** Word style id, e.g. `Heading1`, `Quote`. Blank = Normal. */
  style?: string;
  /** Left indent in twips (1/20 pt); used for lists and quotes. */
  indent?: number;
  /** Draw a bottom border on the paragraph (used for `<hr>`). */
  rule?: boolean;
  /** Render runs in a monospace font (used for `<pre>`). */
  mono?: boolean;
  runs: Run[];
}

interface WalkCtx {
  /** Nesting depth for lists (controls indent). */
  depth: number;
  /** Style inherited from a wrapping block (e.g. Quote inside blockquote). */
  style?: string;
}

const INLINE_TAGS = new Set([
  'a', 'b', 'strong', 'i', 'em', 'u', 'code', 'span', 'sub', 'sup', 'mark',
  'small', 'abbr', 'cite', 'q', 'kbd', 'samp', 'var', 'time', 'del', 's', 'ins',
]);

// ── Public entry ──────────────────────────────────────────────────────────────

/**
 * Build a .docx Blob from a fragment of (already cleaned) HTML.
 * JSZip is imported on demand so it stays out of the page's main chunk.
 */
export async function htmlToDocxBlob(html: string): Promise<Blob> {
  const body = parseToBody(html);

  const paras: Para[] = [];
  const links: Array<{ id: string; url: string }> = [];
  processBlocks(Array.from(body.childNodes), paras, links, { depth: 0 });

  // A valid body needs at least one paragraph.
  if (paras.length === 0) paras.push({ runs: [] });

  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  zip.file('[Content_Types].xml', CONTENT_TYPES);
  zip.file('_rels/.rels', ROOT_RELS);
  zip.file('word/styles.xml', STYLES_XML);
  zip.file('word/_rels/document.xml.rels', documentRels(links));
  zip.file('word/document.xml', documentXml(paras));

  return zip.generateAsync({ type: 'blob', mimeType: DOCX_MIME });
}

// ── Block walk ────────────────────────────────────────────────────────────────

/**
 * Walk block-level nodes into paragraphs. Loose inline content (bare text, a
 * stray `<strong>` under the body) is buffered and flushed as its own paragraph
 * so nothing is dropped.
 */
function processBlocks(
  nodes: Node[],
  out: Para[],
  links: Array<{ id: string; url: string }>,
  ctx: WalkCtx,
): void {
  let buffer: Run[] = [];

  const flush = (): void => {
    if (buffer.some((r) => r.br || r.text.trim() !== '')) {
      out.push({ style: ctx.style, runs: trimRuns(buffer) });
    }
    buffer = [];
  };

  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).data.replace(/\s+/g, ' ');
      if (text.trim() !== '') buffer.push({ text });
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();

    if (INLINE_TAGS.has(tag) || tag === 'br') {
      buffer.push(...inlineRuns([el], {}, links));
    } else {
      flush();
      handleBlock(el, tag, out, links, ctx);
    }
  }

  flush();
}

function handleBlock(
  el: Element,
  tag: string,
  out: Para[],
  links: Array<{ id: string; url: string }>,
  ctx: WalkCtx,
): void {
  const kids = Array.from(el.childNodes);

  switch (tag) {
    case 'h1': case 'h2': case 'h3': case 'h4': case 'h5': case 'h6':
      out.push({ style: `Heading${tag[1]}`, runs: trimRuns(inlineRuns(kids, {}, links)) });
      return;

    case 'p':
      out.push({ style: ctx.style, runs: trimRuns(inlineRuns(kids, {}, links)) });
      return;

    case 'blockquote':
      // Recurse so nested <p> keep their breaks, tagged with the Quote style.
      processBlocks(kids, out, links, { ...ctx, style: 'Quote' });
      return;

    case 'ul': case 'ol':
      handleList(el, tag === 'ol', out, links, ctx);
      return;

    case 'pre': {
      const text = (el.textContent ?? '').replace(/\n+$/, '');
      const runs: Run[] = [];
      text.split('\n').forEach((line, i) => {
        if (i > 0) runs.push({ text: '', br: true });
        if (line) runs.push({ text: line, mono: true });
      });
      out.push({ mono: true, runs });
      return;
    }

    case 'hr':
      out.push({ rule: true, runs: [] });
      return;

    case 'table':
      handleTable(el, out, links);
      return;

    case 'img': {
      const alt = el.getAttribute('alt');
      if (alt) out.push({ style: ctx.style, runs: [{ text: `[${alt}]` }] });
      return;
    }

    default:
      // Generic container (div, section, article, header, li-less wrappers…).
      processBlocks(kids, out, links, ctx);
      return;
  }
}

function handleList(
  listEl: Element,
  ordered: boolean,
  out: Para[],
  links: Array<{ id: string; url: string }>,
  ctx: WalkCtx,
): void {
  const depth = ctx.depth;
  let index = Number(listEl.getAttribute('start') ?? '1') || 1;

  for (const li of Array.from(listEl.children)) {
    if (li.tagName.toLowerCase() !== 'li') continue;

    const prefix = ordered ? `${index++}.\t` : '•\t';
    const inlineKids = Array.from(li.childNodes).filter(
      (n) => !(n.nodeType === Node.ELEMENT_NODE
        && ['ul', 'ol'].includes((n as Element).tagName.toLowerCase())),
    );

    const runs = trimRuns(inlineRuns(inlineKids, {}, links));
    out.push({ indent: 360 * (depth + 1), runs: [{ text: prefix }, ...runs] });

    // Nested lists carry on one level deeper.
    for (const child of Array.from(li.children)) {
      const childTag = child.tagName.toLowerCase();
      if (childTag === 'ul' || childTag === 'ol') {
        handleList(child, childTag === 'ol', out, links, { ...ctx, depth: depth + 1 });
      }
    }
  }
}

function handleTable(
  tableEl: Element,
  out: Para[],
  links: Array<{ id: string; url: string }>,
): void {
  for (const row of Array.from(tableEl.querySelectorAll('tr'))) {
    const runs: Run[] = [];
    const cells = Array.from(row.children).filter((c) =>
      ['td', 'th'].includes(c.tagName.toLowerCase()));
    cells.forEach((cell, i) => {
      if (i > 0) runs.push({ text: '\t' });
      const header = cell.tagName.toLowerCase() === 'th';
      runs.push(...inlineRuns(Array.from(cell.childNodes), { bold: header }, links));
    });
    if (runs.length) out.push({ runs: trimRuns(runs) });
  }
}

// ── Inline walk ───────────────────────────────────────────────────────────────

function inlineRuns(
  nodes: Node[],
  fmt: Partial<Run>,
  links: Array<{ id: string; url: string }>,
): Run[] {
  const runs: Run[] = [];

  for (const node of nodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node as Text).data.replace(/\s+/g, ' ');
      if (text !== '') runs.push({ ...fmt, text });
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    const kids = Array.from(el.childNodes);

    switch (tag) {
      case 'br':
        runs.push({ text: '', br: true });
        break;
      case 'strong': case 'b':
        runs.push(...inlineRuns(kids, { ...fmt, bold: true }, links));
        break;
      case 'em': case 'i': case 'cite': case 'var':
        runs.push(...inlineRuns(kids, { ...fmt, italic: true }, links));
        break;
      case 'u': case 'ins':
        runs.push(...inlineRuns(kids, { ...fmt, underline: true }, links));
        break;
      case 'code': case 'kbd': case 'samp':
        runs.push(...inlineRuns(kids, { ...fmt, mono: true }, links));
        break;
      case 'a': {
        const href = el.getAttribute('href');
        if (href && /^(https?:|mailto:)/i.test(href)) {
          const id = `rId${links.length + 2}`; // rId1 is the stylesheet
          links.push({ id, url: href });
          runs.push(...inlineRuns(kids, { ...fmt, linkId: id }, links));
        } else {
          runs.push(...inlineRuns(kids, fmt, links));
        }
        break;
      }
      default:
        runs.push(...inlineRuns(kids, fmt, links));
        break;
    }
  }

  return runs;
}

/** Drop leading/trailing blank runs and collapse the seam whitespace. */
function trimRuns(runs: Run[]): Run[] {
  const out = runs.filter((r) => r.br || r.text !== '');
  while (out.length && !out[0].br && out[0].text.trim() === '') out.shift();
  while (out.length && !out[out.length - 1].br && out[out.length - 1].text.trim() === '') out.pop();
  return out;
}

// ── Serialisation ─────────────────────────────────────────────────────────────

function documentXml(paras: Para[]): string {
  const body = paras.map(paraXml).join('');
  return `${XML_DECL}<w:document xmlns:w="${W_NS}" xmlns:r="${R_NS}"><w:body>${body}`
    + '<w:sectPr><w:pgSz w:w="12240" w:h="15840"/>'
    + '<w:pgMar w:top="1440" w:right="1440" w:bottom="1440" w:left="1440"/></w:sectPr>'
    + '</w:body></w:document>';
}

function paraXml(p: Para): string {
  const pPr: string[] = [];
  if (p.style) pPr.push(`<w:pStyle w:val="${p.style}"/>`);
  if (p.indent) pPr.push(`<w:ind w:left="${p.indent}"/>`);
  if (p.rule) {
    pPr.push('<w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="auto"/></w:pBdr>');
  }
  const pPrXml = pPr.length ? `<w:pPr>${pPr.join('')}</w:pPr>` : '';

  // Group consecutive runs that share a hyperlink into one <w:hyperlink>.
  let inner = '';
  let i = 0;
  while (i < p.runs.length) {
    const linkId = p.runs[i].linkId;
    if (linkId) {
      const group: Run[] = [];
      while (i < p.runs.length && p.runs[i].linkId === linkId) group.push(p.runs[i++]);
      inner += `<w:hyperlink r:id="${linkId}">${group.map((r) => runXml(r, p.mono)).join('')}</w:hyperlink>`;
    } else {
      inner += runXml(p.runs[i++], p.mono);
    }
  }

  return `<w:p>${pPrXml}${inner}</w:p>`;
}

function runXml(r: Run, paraMono?: boolean): string {
  if (r.br) return '<w:r><w:br/></w:r>';

  const rPr: string[] = [];
  if (r.bold) rPr.push('<w:b/>');
  if (r.italic) rPr.push('<w:i/>');
  if (r.underline || r.linkId) rPr.push('<w:u w:val="single"/>');
  if (r.linkId) rPr.push('<w:color w:val="0563C1"/>');
  if (r.mono || paraMono) rPr.push('<w:rFonts w:ascii="Consolas" w:hAnsi="Consolas" w:cs="Consolas"/>');
  const rPrXml = rPr.length ? `<w:rPr>${rPr.join('')}</w:rPr>` : '';

  return `<w:r>${rPrXml}<w:t xml:space="preserve">${escXml(r.text)}</w:t></w:r>`;
}

function documentRels(links: Array<{ id: string; url: string }>): string {
  const hyperlinks = links
    .map((l) =>
      `<Relationship Id="${l.id}" Type="${REL_NS}/hyperlink" Target="${escXml(l.url)}" TargetMode="External"/>`)
    .join('');
  return `${XML_DECL}<Relationships xmlns="${PKG_REL_NS}">`
    + `<Relationship Id="rId1" Type="${REL_NS}/styles" Target="styles.xml"/>`
    + `${hyperlinks}</Relationships>`;
}

/** Escape text for XML and drop characters XML forbids (old control codes). */
function escXml(s: string): string {
  let out = '';
  for (const ch of s) {
    const code = ch.codePointAt(0)!;
    // Drop control codes XML 1.0 forbids; keep tab (9), newline (10), CR (13).
    if (code < 0x20 && code !== 9 && code !== 10 && code !== 13) continue;
    if (ch === '&') out += '&amp;';
    else if (ch === '<') out += '&lt;';
    else if (ch === '>') out += '&gt;';
    else if (ch === '"') out += '&quot;';
    else out += ch;
  }
  return out;
}

// ── Static parts ──────────────────────────────────────────────────────────────

const XML_DECL = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';
const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
const R_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const REL_NS = 'http://schemas.openxmlformats.org/officeDocument/2006/relationships';
const PKG_REL_NS = 'http://schemas.openxmlformats.org/package/2006/relationships';

const CONTENT_TYPES = `${XML_DECL}<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">`
  + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
  + '<Default Extension="xml" ContentType="application/xml"/>'
  + '<Override PartName="/word/document.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml"/>'
  + '<Override PartName="/word/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml"/>'
  + '</Types>';

const ROOT_RELS = `${XML_DECL}<Relationships xmlns="${PKG_REL_NS}">`
  + `<Relationship Id="rId1" Type="${REL_NS}/officeDocument" Target="word/document.xml"/>`
  + '</Relationships>';

/** Heading sizes in half-points: h1 18pt down to h6 11pt. */
const HEADING_SIZES = [36, 32, 28, 26, 24, 22];

const HEADING_STYLES = HEADING_SIZES.map((sz, i) => {
  const n = i + 1;
  return `<w:style w:type="paragraph" w:styleId="Heading${n}"><w:name w:val="heading ${n}"/>`
    + '<w:basedOn w:val="Normal"/><w:pPr><w:keepNext/><w:spacing w:before="240" w:after="60"/></w:pPr>'
    + `<w:rPr><w:b/><w:color w:val="2E74B5"/><w:sz w:val="${sz}"/></w:rPr></w:style>`;
}).join('');

const STYLES_XML = `${XML_DECL}<w:styles xmlns:w="${W_NS}">`
  + '<w:docDefaults><w:rPrDefault><w:rPr>'
  + '<w:rFonts w:ascii="Calibri" w:hAnsi="Calibri" w:cs="Calibri"/><w:sz w:val="22"/>'
  + '</w:rPr></w:rPrDefault></w:docDefaults>'
  + '<w:style w:type="paragraph" w:default="1" w:styleId="Normal"><w:name w:val="Normal"/>'
  + '<w:pPr><w:spacing w:after="160" w:line="259" w:lineRule="auto"/></w:pPr></w:style>'
  + HEADING_STYLES
  + '<w:style w:type="paragraph" w:styleId="Quote"><w:name w:val="Quote"/><w:basedOn w:val="Normal"/>'
  + '<w:pPr><w:ind w:left="720"/></w:pPr><w:rPr><w:i/><w:color w:val="666666"/></w:rPr></w:style>'
  + '</w:styles>';
